import React, { useMemo, useState } from "react";
import { useAppSelector, useAppDispatch } from "@/app/hooks";
import {
  selectBoardState,
  selectDebouncedSearchQuery,
  selectGroupingConfig,
  selectPagination,
} from "@/features/bughurd/boardSelector";
import { setPagination } from "@/features/bughurd/slice";
import { useGetListOfBugsQuery } from "@/features/bughurd/api";
import { useParams } from "react-router-dom";
import { useBoardFilters } from "./useBoardFilters";
import {
  Box,
  IconButton,
  Typography,
  Chip,
  Avatar,
  AvatarGroup,
  Tooltip,
  CircularProgress,
  useTheme,
  Menu,
  MenuItem,
} from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import {
  RadioButtonUnchecked,
  RadioButtonChecked,
  Person,
  CalendarToday,
  MoreVert,
  ChatBubbleOutline,
  Edit,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import moment from "moment";
import Pagination from "@/Components/Common/TablePro/Pagination";
import { Icon } from "@iconify/react";
import TaskUpdateDrawer from "@/pages/Board/bugherdComponents/TaskUpdateDrawer";
import { colorPalette, typeToIconMapping } from "@/pages/Board/utils";
import HighestIcon from "@/assets/icons/highest.svg?react";
import HighIcon from "@/assets/icons/high.svg?react";
import MediumIcon from "@/assets/icons/medium.svg?react";
import LowIcon from "@/assets/icons/low.svg?react";
import {
  useUpdateBugDetailMutation,
  useDeleteBugMutation,
  useGetCommentsByBugIdQuery,
} from "@/features/bughurd/api";
import { useGetCompanyMembersQuery } from "@/features/Members/api";
import DeleteConfirmModal from "@/Components/Common/DeleteConfirmModal";
import CommentsPopover from "../CommentsPopover";

interface RecurringTaskTabProps {
  handlePageChange: (
    columnId: string | null,
    paginationModel: any,
    viewType: string,
    subGroupId?: string
  ) => void;
  refreshColumnData: (columnId?: string, subGroupId?: string) => Promise<void>;
  isMyTaskView?: boolean;
}

const RecurringTaskTab: React.FC<RecurringTaskTabProps> = ({
  handlePageChange,
  refreshColumnData,
}) => {
  const { t } = useTranslation(["bugherd"]);
  const { id } = useParams<{ id: string }>();
  const theme = useTheme();
  const dispatch = useAppDispatch();

  // Redux selectors
  const groupingConfig = useAppSelector(selectGroupingConfig);
  const pagination = useAppSelector(selectPagination);
  const { recordsPerPage, dateFormat } = useAppSelector(
    (state) => state.appGlobal
  );
  const { filters } = useBoardFilters();
  const debouncedSearchQuery = useAppSelector(selectDebouncedSearchQuery);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: recordsPerPage || 10,
    page: 1,
  });

  // Extract filters
  const {
    selectedTags,
    selectedAvatars,
    selectedStatuses,
    selectedPriorities,
    selectedProjects,
    dueDateStart,
    dueDateEnd,
    startDateStart,
    startDateEnd,
    updatedAtStart,
    updatedAtEnd,
    selectedCreatedBy,
    isRecurring,
  } = filters;

  const { groupBy } = groupingConfig;
  const PAGE_LIMIT = recordsPerPage || 25;

  // Local state
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [actionTaskId, setActionTaskId] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<string | null>(null);
  const [commentsAnchorEl, setCommentsAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [commentTaskId, setCommentTaskId] = useState<string | null>(null);
  const [currentTaskName, setCurrentTaskName] = useState("");

  // API hooks
  const {
    data: listOfBugsData,
    isLoading: isBugsLoadingQuery,
    isFetching: isBugsFetchingQuery,
    refetch: refetchBugList,
  } = useGetListOfBugsQuery({
    id: id || "",
    forBoard: true,
    search: debouncedSearchQuery || "",
    assignTo: selectedAvatars?.length > 0 ? selectedAvatars : undefined,
    tags: selectedTags?.length > 0 ? selectedTags : undefined,
    statuses: selectedStatuses?.length > 0 ? selectedStatuses : undefined,
    severities: selectedPriorities?.length > 0 ? selectedPriorities : undefined,
    createdBy: selectedCreatedBy?.length > 0 ? selectedCreatedBy : undefined,
    page: paginationModel.page,
    limit: paginationModel.pageSize,
    isRecurring: true,
    ...(dueDateStart && { dueDateStart }),
    ...(dueDateEnd && { dueDateEnd }),
    ...(startDateStart && { startDateStart }),
    ...(startDateEnd && { startDateEnd }),
    ...(updatedAtStart && { updatedAtStart }),
    ...(updatedAtEnd && { updatedAtEnd }),
  });

  const { data: users } = useGetCompanyMembersQuery(1);
  const [updateBug, { isLoading: isUpdating }] = useUpdateBugDetailMutation();
  const [deleteBug, { isLoading: isDeleting }] = useDeleteBugMutation();

  // Fetch comments for selected task
  const { data: commentsData, isLoading: isLoadingComments } =
    useGetCommentsByBugIdQuery(
      { bugId: commentTaskId },
      { skip: !commentTaskId, refetchOnMountOrArgChange: true }
    );

  const comments = commentsData?.data?.decryptedData?.items || [];

  // Convert task data to row format
  const convertTasksToRows = (tasks: any[]) => {
    return (
      tasks?.map((task) => ({
        id: task._id,
        _id: task._id,
        summary: task.summary || "",
        assignTo: task.assignTo || [],
        bugStatus: task.bugStatus || "",
        dueDate: task.dueDate || "",
        severity: task.severity || "",
        commentCount: task.commentCount || 0,
        type: task.type || "",
        bugKey: task.bugKey || "",
        createdBy: task.createdBy || {},
        createdAt: task.createdAt || "",
        updatedAt: task.updatedAt || "",
        isRecurring: task.isRecurring || false,
      })) || []
    );
  };

  // Get rows data
  const rowsData = convertTasksToRows(
    listOfBugsData?.data?.decryptedData?.items || []
  );
  const totalRows = listOfBugsData?.data?.decryptedData?.total || 0;

  // Helper functions
  const getGlobalPaginationModel = () => {
    const globalPagination = pagination["global"];
    return {
      page: Math.max(0, (globalPagination?.page || 1) - 1),
      pageSize: globalPagination?.limit || PAGE_LIMIT,
    };
  };

  const isGlobalLoading = () => {
    return (
      isBugsLoadingQuery || isBugsFetchingQuery || isDeleting || isUpdating
    );
  };

  // Handle pagination
  const handleGlobalPaginationChange = (newPaginationModel: any) => {
    const updatedModel = {
      ...newPaginationModel,
      page: Math.max(1, newPaginationModel.page),
    };

    dispatch(
      setPagination({
        columnId: "global",
        pagination: updatedModel,
      })
    );
  };

  // Row update handler
  const processRowUpdate = async (newRow: any, oldRow: any) => {
    try {
      if (newRow.summary !== oldRow.summary) {
        await updateBug({
          bugId: newRow.id,
          summary: newRow.summary,
        }).unwrap();
        toast.success(t("Task updated successfully"));
      }
      return newRow;
    } catch (error) {
      console.error("Error updating task:", error);
      toast.error(t("Failed to update task name"));
      return oldRow;
    }
  };

  // Action handlers
  const handleActionMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    taskId: string
  ) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setActionTaskId(taskId);
  };

  const handleActionMenuClose = () => {
    setMenuAnchorEl(null);
    setActionTaskId(null);
  };

  const handleEditTask = (taskId: string) => {
    handleActionMenuClose();
    setSelectedTaskId(taskId);
    setIsTaskModalOpen(true);
  };

  const openDeleteModal = (taskId: string) => {
    handleActionMenuClose();
    setTaskToDelete(taskId);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setTaskToDelete(null);
  };

  const handleDeleteTask = async () => {
    if (!taskToDelete) return;

    try {
      await deleteBug(taskToDelete).unwrap();
      toast.success(t("Task deleted successfully"));
      refetchBugList();
      closeDeleteModal();
    } catch (error) {
      console.error("Error deleting task:", error);
      toast.error(t("Failed to delete task"));
      closeDeleteModal();
    }
  };

  const handleCloseTaskModal = () => {
    setIsTaskModalOpen(false);
    setSelectedTaskId(null);
  };

  // Comments handlers
  const handleViewComments = (
    event: React.MouseEvent,
    taskId: string,
    taskName: string
  ) => {
    event.stopPropagation();
    setCommentTaskId(taskId);
    setCurrentTaskName(taskName);
    setCommentsAnchorEl(event.currentTarget);
  };

  const handleCloseComments = () => {
    setCommentsAnchorEl(null);
    setCommentTaskId(null);
    setCurrentTaskName("");
  };

  // Find task helper
  const findTask = (taskId: string) => {
    return rowsData.find((task) => task._id === taskId);
  };

  // Render functions
  const StatusIcon = ({ iconName, size = 24 }) => {
    if (!iconName) return null;
    return (
      <Icon
        icon={iconName}
        width={size}
        height={size}
        style={{
          color:
            theme.palette.mode === "dark"
              ? theme.palette.background.paper
              : "text.primary",
          filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.1))",
        }}
      />
    );
  };

  const renderPriority = (severity: string) => {
    const commonBoxStyles = {
      display: "flex",
      alignItems: "center",
      height: "100%",
      padding: "4px 0",
    };

    switch (severity) {
      case "Highest":
        return (
          <Box sx={{ ...commonBoxStyles, color: "error.main" }}>
            <HighestIcon style={{ width: 20, height: 20, marginRight: 8 }} />
            <Typography variant="body2" color="error">
              {t("Highest")}
            </Typography>
          </Box>
        );
      case "High":
        return (
          <Box sx={{ ...commonBoxStyles, color: "warning.main" }}>
            <HighIcon style={{ width: 20, height: 20, marginRight: 8 }} />
            <Typography variant="body2" color="warning.dark">
              {t("High")}
            </Typography>
          </Box>
        );
      case "Medium":
        return (
          <Box sx={{ ...commonBoxStyles, color: "info.main" }}>
            <MediumIcon style={{ width: 20, height: 20, marginRight: 8 }} />
            <Typography variant="body2" color="info.main">
              {t("Medium")}
            </Typography>
          </Box>
        );
      case "Low":
        return (
          <Box sx={{ ...commonBoxStyles, color: "success.main" }}>
            <LowIcon style={{ width: 20, height: 20, marginRight: 8 }} />
            <Typography variant="body2" color="success.main">
              {t("Low")}
            </Typography>
          </Box>
        );
      default:
        return null;
    }
  };

  const renderAssignee = (assignees: any[]) => {
    if (assignees && assignees.length > 0) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            height: "100%",
          }}
        >
          <AvatarGroup
            max={3}
            spacing="medium"
            sx={{
              "& .MuiAvatar-root": {
                width: 24,
                height: 24,
                fontSize: 12,
              },
            }}
          >
            {assignees.map((user, index) => (
              <Tooltip key={user._id} title={user.fullName}>
                <Avatar
                  sx={{ bgcolor: colorPalette[index % colorPalette.length] }}
                >
                  {user.fullName?.charAt(0)}
                </Avatar>
              </Tooltip>
            ))}
          </AvatarGroup>
        </Box>
      );
    } else {
      return (
        <Box
          sx={{
            color: "text.secondary",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            height: "100%",
          }}
        >
          <Person fontSize="small" />
          <Typography variant="body2">{t("Unassigned")}</Typography>
        </Box>
      );
    }
  };

  const renderDueDate = (dueDate: string) => {
    if (dueDate) {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
          }}
        >
          <Typography variant="body2">
            {moment(dueDate).isValid() &&
              moment(dueDate).format(dateFormat || "DD MMM, YYYY")}
          </Typography>
        </Box>
      );
    } else {
      return (
        <Box
          sx={{
            color: "text.secondary",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            height: "100%",
          }}
        >
          <CalendarToday fontSize="small" />
          <Typography variant="body2">{t("No date")}</Typography>
        </Box>
      );
    }
  };

  const renderComments = (
    commentCount: number,
    taskId: string,
    taskName: string
  ) => {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          cursor: "pointer",
          height: "100%",
          justifyContent: "center",
        }}
        onClick={(event) => handleViewComments(event, taskId, taskName)}
      >
        <ChatBubbleOutline sx={{ fontSize: 16, color: "text.secondary" }} />
        <Typography variant="body2" sx={{ lineHeight: 1 }}>
          {commentCount || "0"}
        </Typography>
      </Box>
    );
  };

  // Memoized grid columns
  const gridColumns = useMemo(
    () => [
      {
        field: "summary",
        headerName: t("Name"),
        flex: 2,
        editable: true,
        renderCell: (params: any) => {
          const type = params.row?.type || "";
          const typeInfo = type
            ? typeToIconMapping[type]
            : typeToIconMapping.TASK;
          const displayText = params.row?.bugKey
            ? `${params.row.bugKey}: ${params.value}`
            : params.value;

          return (
            <Tooltip
              title={displayText}
              placement="top-start"
              arrow
              enterDelay={500}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  width: "100%",
                  height: "100%",
                  cursor: "pointer",
                }}
                onClick={() => handleEditTask(params.row._id)}
              >
                {type && (
                  <Box
                    component="span"
                    sx={{
                      width: 16,
                      height: 16,
                      mr: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: typeInfo.bgcolor,
                      borderRadius: "3px",
                      color: "white",
                    }}
                  >
                    {typeInfo.icon}
                  </Box>
                )}
                <Typography
                  variant="body2"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {params.row?.bugKey && (
                    <span style={{ marginRight: "4px", color: "#1976D2" }}>
                      {params.row.bugKey}:
                    </span>
                  )}
                  {params.value}
                </Typography>
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: "assignTo",
        headerName: t("Assignee"),
        flex: 1,
        renderCell: (params: any) => renderAssignee(params.value),
        sortComparator: (v1: any, v2: any) => {
          if (!v1 || v1.length === 0) {
            if (!v2 || v2.length === 0) return 0;
            return 1;
          }
          if (!v2 || v2.length === 0) return -1;

          const name1 = v1[0]?.fullName?.toLowerCase() || "";
          const name2 = v2[0]?.fullName?.toLowerCase() || "";

          return name1.localeCompare(name2);
        },
      },
      {
        field: "bugStatus",
        headerName: t("Status"),
        flex: 1,
        renderCell: (params: any) => (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography variant="body2" color="text.secondary">
              {params.value?.status}
            </Typography>
          </Box>
        ),
      },
      {
        field: "dueDate",
        headerName: t("Due date"),
        flex: 1,
        renderCell: (params: any) => renderDueDate(params.value),
      },
      {
        field: "severity",
        headerName: t("Priority"),
        flex: 1,
        renderCell: (params: any) => renderPriority(params.value),
      },
      {
        field: "commentCount",
        headerName: t("Comments"),
        width: 100,
        renderCell: (params: any) => {
          const task = findTask(params.id);
          return renderComments(params.value, params.id, task?.summary || "");
        },
      },
      {
        field: "actions",
        headerName: "",
        width: 50,
        renderCell: (params: any) => (
          <IconButton
            size="small"
            onClick={(event) => handleActionMenuOpen(event, params.id)}
          >
            <MoreVert fontSize="small" />
          </IconButton>
        ),
      },
    ],
    [t, theme, dateFormat]
  );

  const selectedTask = findTask(selectedTaskId || "");
  const taskToDeleteName = findTask(taskToDelete || "")?.summary || "";

  return (
    <Box
      sx={{
        height: "calc(100vh - 240px)",
        width: "100%",
        position: "relative",
      }}
    >
      <DataGridPro
        rows={rowsData}
        columns={gridColumns}
        loading={isGlobalLoading()}
        disableRowSelectionOnClick
        pagination
        paginationMode="server"
        rowCount={totalRows}
        paginationModel={getGlobalPaginationModel()}
        onPaginationModelChange={(model) => {
          handleGlobalPaginationChange({
            page: model.page + 1,
            limit: model.pageSize,
          });
        }}
        pageSizeOptions={[10, 20, 25, 50, 100]}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={(error) => {
          console.error("Error processing row update:", error);
          toast.error(t("Failed to update task"));
        }}
        //@ts-ignore
        experimentalFeatures={{ newEditingApi: true }}
        disableColumnMenu
        disableVirtualization={false}
        slots={{
          pagination: () => (
            <Pagination
              total={totalRows || 10}
              paginationModel={paginationModel}
              setPaginationModel={setPaginationModel}
            />
          ),
        }}
        sx={{
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
          "& .MuiDataGrid-cell:focus": {
            outline: "none",
          },
          "& .MuiDataGrid-cell:focus-within": {
            outline: "none",
          },
          "& .MuiDataGrid-cell--editing": {
            boxShadow: "none !important",
            outline: "none !important",
            padding: "0 !important",
            backgroundColor: "transparent !important",
          },
          border: "none",
        }}
      />

      {/* Action Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleActionMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={() => handleEditTask(actionTaskId || "")}>
          <Typography variant="body2">{t("Edit")}</Typography>
        </MenuItem>
        <MenuItem onClick={() => openDeleteModal(actionTaskId || "")}>
          <Typography variant="body2">{t("Delete")}</Typography>
        </MenuItem>
      </Menu>

      {/* Comments Popover */}
      <CommentsPopover
        anchorEl={commentsAnchorEl}
        onClose={handleCloseComments}
        comments={comments}
        taskId={commentTaskId}
        isLoading={isLoadingComments}
        taskName={currentTaskName}
      />

      {/* Task Edit Modal/Drawer */}
      {selectedTaskId && isTaskModalOpen && (
        <TaskUpdateDrawer
          open={isTaskModalOpen}
          onClose={handleCloseTaskModal}
          setBugId={() => {}}
          setDrawerOpen={() => {}}
          bugId={selectedTaskId}
          users={users?.data?.decryptedData || []}
          createdBy={selectedTask?.createdBy?.fullName || ""}
          setCreatedBy={() => {}}
          refreshColumn={() => {
            refetchBugList();
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        open={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteTask}
        loading={isDeleting}
        title={t("Delete Task")}
        confirmMessage={t("Are you sure you want to delete this task?")}
        okText={t("Delete")}
      />
    </Box>
  );
};

export default RecurringTaskTab;
